import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// Layout
import Header from '../layout/header';
import Footer from '../layout/footer';

// Elements
import AvailableCitiesModal from '../elements/available-cities-modal';
import CareersApplyForm from '../elements/careers-apply-form';

// Premium design system
import { SectionWrapper, Breadcrumbs, StructuredData } from '../elements/premium';

import careersData from '../../data/careers.json';

const OPENINGS = careersData.openings || [];
const ORIGIN = 'https://www.ridenrepair.com';

// Cities we hire in, mapped to the region Google expects in a PostalAddress.
const CITY_REGION = {
  Bengaluru: 'Karnataka',
  Mumbai: 'Maharashtra',
  Pune: 'Maharashtra',
  Delhi: 'Delhi',
  Gurugram: 'Haryana',
  Noida: 'Uttar Pradesh',
  Hyderabad: 'Telangana',
  Chennai: 'Tamil Nadu',
};

const esc = (s) =>
  String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/** Build the HTML description string Google's JobPosting schema expects. */
function buildJobDescriptionHtml(job) {
  const list = (items) => `<ul>${items.map((i) => `<li>${esc(i)}</li>`).join('')}</ul>`;
  let html = `<p>${esc(job.about)}</p>`;
  html += `<h3>What you will do</h3>${list(job.responsibilities || [])}`;
  html += `<h3>What we are looking for</h3>${list(job.requirements || [])}`;
  if (job.nice_to_have && job.nice_to_have.length) {
    html += `<h3>Nice to have</h3>${list(job.nice_to_have)}`;
  }
  return html;
}

class CareerDetails extends Component {
  render() {
    const { isMobileBrowser, isMobileApp, user, setUser, citiesModal, setCitiesModal, match } = this.props;
    const slug = (match && match.params && match.params.jobSlug) || '';
    const job = OPENINGS.find((j) => j.slug === slug);

    const chrome = (children, helmet) => (
      <div className="rr-home rr-home--lux rr-careers rr-careers--detail">
        {helmet}
        {citiesModal && <AvailableCitiesModal closeModal={() => setCitiesModal(false)} />}
        <Header isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} user={user} setUser={setUser} />
        {children}
        <Footer isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} setCitiesModal={setCitiesModal} />
      </div>
    );

    // Unknown or retired role — never index a dead job URL, always give a way back.
    if (!job) {
      return chrome(
        <SectionWrapper kicker="Careers" heading="This role is no longer open" narrow>
          <p className="rr-careers-detail__gone">
            The position you were looking for has been filled or closed. See what we are hiring for right now.
          </p>
          <p style={{ textAlign: 'center' }}>
            <Link to="/careers" className="rr-cta-primary">View all open roles</Link>
          </p>
        </SectionWrapper>,
        <Helmet>
          <title>Role Closed | Careers at Ride N Repair</title>
          <meta name="robots" content="noindex,follow" />
          <link rel="canonical" href={`${ORIGIN}/careers`} />
        </Helmet>
      );
    }

    const canonical = `${ORIGIN}/careers/${job.slug}`;
    const locationLine = job.locations.join(', ');

    const jobPosting = {
      '@type': 'JobPosting',
      title: job.title,
      description: buildJobDescriptionHtml(job),
      identifier: {
        '@type': 'PropertyValue',
        name: 'Ride N Repair',
        value: job.slug,
      },
      datePosted: job.posted_on,
      validThrough: `${job.valid_through}T23:59:59+05:30`,
      employmentType: job.employment_type,
      hiringOrganization: {
        '@type': 'Organization',
        name: 'Ride N Repair',
        sameAs: ORIGIN,
        // /images/logo.webp 404s (site-wide schema bug, tracked separately) — Google
        // rejects a JobPosting whose hiringOrganization.logo does not resolve.
        logo: `${ORIGIN}/logo192.png`,
      },
      jobLocation: job.locations.map((city) => ({
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressLocality: city,
          ...(CITY_REGION[city] ? { addressRegion: CITY_REGION[city] } : {}),
          addressCountry: 'IN',
        },
      })),
      totalJobOpenings: job.openings_count,
      industry: 'Automotive Service and Repair',
      directApply: true,
      url: canonical,
    };

    return chrome(
      <>
        <StructuredData data={jobPosting} />

        <Breadcrumbs
          items={[{ label: 'Home', href: '/' }, { label: 'Careers', href: '/careers' }, { label: job.title }]}
        />

        {/* ROLE HEADER */}
        <section className="rr-role-hero">
          <div className="rr-container">
            <Link to="/careers" className="rr-role-hero__back">← All open roles</Link>
            <p className="rr-kicker">{job.department}</p>
            <h1 className="rr-role-hero__title">{job.title}</h1>
            <p className="rr-role-hero__summary">{job.summary}</p>
            <ul className="rr-role-hero__facts">
              <li><span>Locations</span><strong>{locationLine}</strong></li>
              <li><span>Experience</span><strong>{job.experience}</strong></li>
              <li><span>Type</span><strong>{job.type_label}</strong></li>
              <li><span>Work mode</span><strong>{job.work_mode}</strong></li>
              {job.openings_count > 1 && <li><span>Openings</span><strong>{job.openings_count}</strong></li>}
            </ul>
            <a href="#apply" className="rr-cta-primary rr-role-hero__cta">Apply for this role</a>
          </div>
        </section>

        {/* ROLE BODY + STICKY APPLY */}
        <section className="rr-section rr-role-body">
          <div className="rr-container">
            <div className="rr-role-grid">
              <article className="rr-role-content">
                <h2>About the role</h2>
                <p>{job.about}</p>

                <h2>What you will do</h2>
                <ul className="rr-role-list">
                  {job.responsibilities.map((r, i) => <li key={i}>{r}</li>)}
                </ul>

                <h2>What we are looking for</h2>
                <ul className="rr-role-list">
                  {job.requirements.map((r, i) => <li key={i}>{r}</li>)}
                </ul>

                {job.nice_to_have && job.nice_to_have.length > 0 && (
                  <>
                    <h2>Nice to have</h2>
                    <ul className="rr-role-list">
                      {job.nice_to_have.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </>
                )}

                <h2>What we offer</h2>
                <ul className="rr-role-list">
                  <li>Structured onboarding and role-specific training before you are on your own.</li>
                  <li>Clear performance measures and a promotion path that is written down, not implied.</li>
                  <li>Company-issued tools, parts and the app that runs every job end to end.</li>
                  <li>A team that answers the phone when you need help mid-job.</li>
                </ul>

                <div className="rr-role-other">
                  <h2>Other open roles</h2>
                  <ul className="rr-role-other__list">
                    {OPENINGS.filter((o) => o.slug !== job.slug).slice(0, 5).map((o) => (
                      <li key={o.slug}>
                        <Link to={`/careers/${o.slug}`}>{o.title}</Link>
                        <span>{o.locations.slice(0, 2).join(' · ')}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/careers" className="rr-role-other__all">View all roles →</Link>
                </div>
              </article>

              <aside className="rr-role-aside" id="apply">
                <div className="rr-role-applyCard">
                  <h2 className="rr-role-applyCard__title">Apply for this role</h2>
                  <p className="rr-role-applyCard__sub">
                    Shortlisted candidates hear back within 7 working days.
                  </p>
                  <CareersApplyForm
                    jobs={[{ slug: job.slug, title: job.title, department: job.department }]}
                    defaultSlug={job.slug}
                    lockRole
                    compact
                    source="web_careers_job_page"
                  />
                </div>
              </aside>
            </div>
          </div>
        </section>
      </>,
      <Helmet>
        <link rel="canonical" href={canonical} />
        <title>{`${job.title} Job in ${job.locations[0]} | Careers at Ride N Repair`}</title>
        <meta
          name="description"
          content={`${job.title} at Ride N Repair — ${job.type_label}, ${job.experience} experience, ${locationLine}. ${job.summary} Apply online.`}
        />
        <meta name="robots" content="index,follow" />
        <meta property="og:title" content={`${job.title} — Careers at Ride N Repair`} />
        <meta property="og:description" content={job.summary} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${job.title} — Careers at Ride N Repair`} />
        <meta name="twitter:description" content={job.summary} />
      </Helmet>
    );
  }
}

export default CareerDetails;
